import React from 'react'
import { ZdsTextButton } from '@zera/components'
import {
  PropertiesContainer,
  MessageError,
  ButtonsPanel,
  PropertyTitle,
  LoaderPropertyContainer,
  LoaderProperty,
  ContentContainer
} from './styles'
import { NoteProperty } from '../../property/NoteProperty'
import { MaxPropertiesSelectedMessage } from '../../MaxPropertiesSelectedMessage'
import { QuantitativeProperty } from '../../property/QuantitativeProperty'
import { QualitativeProperty } from '../../property/QualitativeProperty'
import { Comment } from '../../property/Comment'
import { ELaboratoryTestPropertyTypes, ILaboratoryTestProperty } from '~/interfaces'
import { useLaboratoryTest } from '~/context'
import { MAX_PROPERTIES_SELECTED } from '~/constants'
import { Skeleton } from '~/components/shared'

const TestPropertiesListLoading = (): JSX.Element => {
  const QUANTITY_OF_PROPERTIES_TO_LOAD = 5

  return (
    <LoaderPropertyContainer>
      <hr />
      <Skeleton containerClassName="title" width="50%" height="25px" />
      {Array.from(Array(QUANTITY_OF_PROPERTIES_TO_LOAD).keys()).map(index => (
        <LoaderProperty key={index}>
          <div>
            <Skeleton className="checkbox" />
            <Skeleton containerClassName="property" height="20px" />
          </div>
          <Skeleton width="80%" height="14px" />
        </LoaderProperty>
      ))}
    </LoaderPropertyContainer>
  )
}

export const TestPropertiesList = (): JSX.Element => {
  const {
    isLoadingProperties,
    laboratoryTestProperties,
    errorLaboratoryTestProperties,
    selectedProperties,
    setSelectedProperties,
    setOpenLaboratoryTestPropertiesComparison
  } = useLaboratoryTest()

  const showCompareButton = selectedProperties.length > 0
  const maximumAmountOfSelectedPropertiesHasBeenNotReached = selectedProperties.length < MAX_PROPERTIES_SELECTED

  const handleOnChange = (property: { name: string; isSelected: boolean; id: number }) => {
    const { isSelected, id } = property

    if (isSelected && maximumAmountOfSelectedPropertiesHasBeenNotReached) {
      setSelectedProperties(prev => [...prev, id])
    } else {
      setSelectedProperties(prev => prev.filter(p => p !== id))
    }
  }

  const handleCompare = () => {
    setOpenLaboratoryTestPropertiesComparison(true)
  }

  if (isLoadingProperties) return <TestPropertiesListLoading />

  if (errorLaboratoryTestProperties) {
    return (
      <>
        <hr />
        <MessageError>{errorLaboratoryTestProperties}</MessageError>
      </>
    )
  }

  const renderFirstDivider = (property: ILaboratoryTestProperty) => {
    const propertyIsNotSection = property?.type !== ELaboratoryTestPropertyTypes.SECTION
    return propertyIsNotSection ? <hr /> : null
  }

  return (
    <PropertiesContainer>
      <MaxPropertiesSelectedMessage type={maximumAmountOfSelectedPropertiesHasBeenNotReached ? 'info' : 'warning'} />

      <ContentContainer>
        {renderFirstDivider(laboratoryTestProperties[0])}

        {laboratoryTestProperties.map((property, index, propertyArray) => {
          const isQualitativeProperty = property.type === ELaboratoryTestPropertyTypes.QUALITATIVE
          const isNoteProperty = property.type === ELaboratoryTestPropertyTypes.NOTE
          const isQuantitativeProperty = property.type === ELaboratoryTestPropertyTypes.QUANTITATIVE
          const isTitle = property.type === ELaboratoryTestPropertyTypes.SECTION
          const nextProperty = propertyArray[index + 1]
          const theSectionHasProperties = nextProperty && nextProperty.type !== ELaboratoryTestPropertyTypes.SECTION

          if (isTitle && theSectionHasProperties) {
            return (
              <React.Fragment key={`${property.code}-${index}`}>
                <hr />
                <PropertyTitle>{property.title}</PropertyTitle>
              </React.Fragment>
            )
          }

          if (isNoteProperty) {
            const { title, sample, code, description, complement } = property
            return (
              <NoteProperty
                key={`qualitative-${sample}-${code}`}
                title={title}
                description={description}
                complement={complement}
              />
            )
          }

          if (isQualitativeProperty) {
            const { referenceValue, name, value, interpretation, complement } = property
            return (
              <QualitativeProperty
                key={`qualitative-${name}-${value}`}
                referenceValue={referenceValue}
                name={name}
                value={value}
                interpretation={interpretation}
                complement={complement}
              />
            )
          }

          if (isQuantitativeProperty) {
            const {
              code,
              name,
              sample,
              prefix,
              value,
              unit,
              lowerReferenceValue,
              upperReferenceValue,
              interpretation,
              complement
            } = property

            return (
              <QuantitativeProperty
                key={`quantitative-${name}-${value}-${unit}-${lowerReferenceValue}`}
                name={name}
                sample={sample}
                prefix={prefix}
                value={value}
                unit={unit}
                id={code}
                lowerReferenceValue={lowerReferenceValue}
                upperReferenceValue={upperReferenceValue}
                interpretation={interpretation}
                complement={complement}
                onChange={handleOnChange}
                selected={selectedProperties.includes(code)}
              />
            )
          }

          const isCommentProperty = property.type === ELaboratoryTestPropertyTypes.COMMENT
          if (isCommentProperty) {
            return <Comment key={`comment-${property.code}`} comment={property} />
          }
        })}
      </ContentContainer>

      {showCompareButton && (
        <ButtonsPanel>
          <ZdsTextButton variant="primary" onClick={handleCompare}>
            Comparar
          </ZdsTextButton>
        </ButtonsPanel>
      )}
    </PropertiesContainer>
  )
}
